import EventBillingCard from "./EventBillingCard";
import { useState } from "react";
import { toast } from "sonner";

import { ChalkActionButton, ChalkCard, SectionTitle } from "../chalk/primitives";
import { tournaments } from "../../lib/tournamentApi";
import RulesEditor from "./RulesEditor";
import { HubGate, Pill, inputStyle } from "./shared";

export default function SettingsTab({ data, onChanged }) {
  const t = data.tournament;
  const [email, setEmail] = useState("");
  async function run(fn, ok) {
    try { await fn(); if (ok) toast.success(ok); await onChanged(); }
    catch (e) { toast.error(e.message || "Couldn't save."); }
  }
  return (
    <div data-testid="settings-tab">
      <EventBillingCard data={data} />
      <SectionTitle label="Plan" />
      <ChalkCard style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }} data-testid="tier-card">
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{t.tier === "hub" ? "The Tournament Hub" : "Whiteboard (free)"}</div>
          <div style={{ fontSize: 12, color: "var(--chalk-muted)" }}>Up to {t.teams_cap} teams per division</div>
        </div>
        <Pill tone={t.tier === "hub" ? "green" : "muted"} testId="tier-pill">{t.tier}</Pill>
      </ChalkCard>
      {t.tier !== "hub" && <HubGate data={data} feature="Hub features" onChanged={onChanged} />}
      {data.capabilities?.admin && <AdminTier t={t} run={run} />}
      <RulesEditor sport={t.sport_key} rules={t.rules} sports={data.sports} locked={false} testPrefix="t-rules"
                   onSave={(body) => run(async () => {
                     const r = await tournaments.patch(t.id, body);
                     if (r.locked_divisions_kept) toast.message(`${r.locked_divisions_kept} locked division${r.locked_divisions_kept > 1 ? "s" : ""} keep their current rules.`);
                   }, "Rules saved.")} />
      {data.divisions.some((d) => d.bracket_locked) && (
        <div data-testid="rules-locked-note" style={{ fontSize: 11, color: "var(--chalk-muted)", margin: "-4px 0 12px" }}>
          Changes apply to unlocked divisions only. {data.divisions.filter((d) => d.bracket_locked).length} locked division(s) keep the rules they started with until reset.
        </div>
      )}
      {t.team_type === "adult" && t.tier === "hub" && (
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, margin: "8px 0 14px" }}>
          <input type="checkbox" checked={t.hide_rosters} data-testid="hide-rosters-toggle"
                 onChange={(e) => run(() => tournaments.patch(t.id, { hide_rosters: e.target.checked }), "Saved.")} />
          Hide rosters on the public page
        </label>
      )}

      <SectionTitle label={`Helpers (${data.helpers.filter((h) => h.status === "active").length}/3)`} />
      {data.helpers.map((h) => (
        <ChalkCard key={h.id} data-testid={`helper-row-${h.id}`} style={{ marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
          <div><b>{h.email}</b> <span style={{ color: "var(--chalk-muted)" }}>· {h.status}</span></div>
          {h.status !== "revoked" && (
            <button aria-label="Revoke helper" data-testid={`helper-revoke-${h.id}`} onClick={() => run(() => tournaments.revokeHelper(t.id, h.id))}
                    style={{ background: "transparent", border: "none", color: "var(--chalk-muted)", cursor: "pointer" }}>×</button>
          )}
        </ChalkCard>
      ))}
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="helper@email.com"
               data-testid="helper-email" style={{ ...inputStyle, flex: 1 }} />
        <ChalkActionButton label="Invite" fullWidth={false} testId="helper-invite"
                           onClick={() => run(() => tournaments.inviteHelper(t.id, email), "Invited.").then(() => setEmail(""))} />
      </div>
      <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 6 }}>
        Helpers can enter scores, schedule games, check teams in and post announcements.
      </div>
      <div style={{ marginTop: 24 }}>
        <button data-testid="tournament-delete"
                onClick={async () => {
                  if (!window.confirm("Delete this tournament? This cannot be undone.")) return;
                  await tournaments.delete(t.id);
                  toast.success("Deleted.");
                  window.location.href = "/app/tournaments";
                }}
                style={{ background: "transparent", color: "#B22222", border: "0.5px solid #B22222",
                         padding: "8px 16px", borderRadius: 8, cursor: "pointer" }}>
          Delete tournament
        </button>
      </div>
    </div>
  );
}

function AdminTier({ t, run }) {
  const [cap, setCap] = useState(t.teams_cap_override || "");
  const set = (tier) => run(() => tournaments.adminTier(t.id, { tier, teams_cap_override: cap ? Number(cap) : null }), "Tier updated.");
  return (
    <ChalkCard data-testid="admin-tier-card" style={{ marginBottom: 12, border: "1px dashed var(--chalk-amber)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--chalk-amber)", textTransform: "uppercase" }}>Admin only</div>
      <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center" }}>
        <input value={cap} onChange={(e) => setCap(e.target.value.replace(/\D/g, ""))} placeholder="Team cap override (65+)"
               data-testid="admin-cap-override" style={{ ...inputStyle, flex: 1 }} />
        <ChalkActionButton label="Set Hub" fullWidth={false} testId="admin-set-hub" onClick={() => set("hub")} />
        <ChalkActionButton label="Set Free" variant="ghost" fullWidth={false} testId="admin-set-whiteboard" onClick={() => set("whiteboard")} />
      </div>
    </ChalkCard>
  );
}
